/**
 * Data layer for the COMPANY panel's Breathwork section.
 * Same page-facing shapes as container/BreathworkHub/breathworkHubApi.js — the
 * two panels are served by the same builders in
 * services/breathworkHubService.js, so both adapt the payload through the
 * shared adapters rather than restating the field mapping here.
 *
 * Backed by the company/v1 breathwork-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js).
 *
 * Every fetcher takes an optional `department` so the section's filter can
 * narrow the whole page to one department.
 */

import {
  CompanyApi,
  getCompanyBreathworkSessionsDashboard,
  getCompanyBreathworkAudioDashboard,
  getCompanyBreathworkUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';
import { adaptSessions, adaptListening } from '../../../BreathworkHub/breathworkHubApi';

export const fetchCompanyBreathworkSessions = async (department = '') => {
  try {
    const data = unwrap(await getCompanyBreathworkSessionsDashboard(department));
    return {
      ...adaptSessions(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees have practised".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyBreathworkSessions error:', error);
    return null;
  }
};

export const fetchCompanyBreathworkAudio = async (department = '') => {
  try {
    const data = unwrap(await getCompanyBreathworkAudioDashboard(department));
    return {
      ...adaptListening(data),
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyBreathworkAudio error:', error);
    return null;
  }
};

// Matches the BreathworkUsersTable fetcher contract.
export const fetchCompanyBreathworkUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await CompanyApi.getCompanyBreathworkUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyBreathworkUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's breathwork profile. Payload shape already matches
 * the shared detail page ({ user, mostPlayedBreathwork, breathworkSessions,
 * topBreathworks, breathworkHistory }); the backend 404s any user outside
 * this company, so no scoping is needed here.
 */
export const fetchCompanyBreathworkUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyBreathworkUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyBreathworkUserDetail error:', error);
    return null;
  }
};
